// src/components/Admin/Settings/Notification_Setting/NotificationSetting.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Edit2,
  MessageCircle,
  Smartphone,
  Mail,
  Bell,
} from "lucide-react";
import useNotification from "@/hooks/useNotification";
import NotificationTemplateDialog from "./NotificationTemplateDialog";

export default function NotificationSettings({ renderActions }) {
  const { getAll, updateStatus, fetchDropdownOptions } = useNotification();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");

  const [templateDialog, setTemplateDialog] = useState({
    open: false,
    type: "sms",
    title: "",
    message: "",
    fields: [],
    record: null,
  });

  // Load data
  const loadNotificationData = async () => {
    setLoading(true);
    try {
      const data = await getAll();
      console.log("notification data:", data);
      setRows(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotificationData();
  }, []);

  // Actions for header
  const actions = {
    onRefresh: loadNotificationData,
  };

  // Call renderActions if provided (for putting buttons in header)
  React.useEffect(() => {
    if (renderActions) {
      renderActions(actions);
    }
  }, [renderActions]);

  // Filter
  const filteredRows = useMemo(() => {
    if (!filterText.trim()) return rows;
    const text = filterText.toLowerCase();
    return rows.filter((r) => {
      const eventText = (r.eventId || "").toLowerCase();
      const moduleText = (r.module || "").toLowerCase();
      const senderText = (r.sender || "").toLowerCase();
      const receiverText = (r.receiver || "").toLowerCase();

      return (
        eventText.includes(text) ||
        moduleText.includes(text) ||
        senderText.includes(text) ||
        receiverText.includes(text)
      );
    });
  }, [rows, filterText]);

  // Toggle WhatsApp / SMS / Email
  const handleToggle = async (row, field, newValue) => {
    if (!row || !row.id) return;

    const originalValue = row[field];
    // optimistic UI
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id ? { ...r, [field]: newValue } : r
      )
    );

    try {
      const updateData = { ...row, [field]: newValue };
      await updateStatus(row.id, updateData);
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      // revert on error
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, [field]: originalValue } : r
        )
      );
    }
  };

  // Single type template view (icon click)
  const openTemplateDialog = (type, row) => {
    let title = "";
    let message = "";

    switch (type) {
      case "sms":
        title = "SMS Template";
        message = "View or modify SMS template.";
        break;
      case "whatsapp":
        title = "WhatsApp Template";
        message = "View or modify WhatsApp template.";
        break;
      case "email":
        title = "Email Template";
        message = "View or modify Email template.";
        break;
      default:
        title = "Template";
    }

    const fields = [];
    if (type === "sms" && row.smsTemplate) {
      fields.push({
        name: "smsTemplate",
        label: "SMS Template",
        type: "input",
        selected: row.smsTemplate,
      });
    }
    if (type === "whatsapp" && row.whatsappTemplate) {
      fields.push({
        name: "whatsappTemplate",
        label: "WhatsApp Template",
        type: "input",
        selected: row.whatsappTemplate,
      });
    }
    if (type === "email" && row.emailTemplate) {
      fields.push({
        name: "emailTemplate",
        label: "Email Template",
        type: "input",
        selected: row.emailTemplate,
      });
    }

    setTemplateDialog({
      open: true,
      type,
      title,
      message,
      fields,
      record: row,
    });
  };

  // Edit (Add/Edit Template) - 3 dropdowns
  const openAddEditDialog = async (row) => {
    const dropdownData = await fetchDropdownOptions();

    setTemplateDialog({
      open: true,
      type: "template",
      title: row ? "Edit Template" : "Add Template",
      message: "",
      record: row,
      fields: [
        {
          name: "whatsappTemplate",
          label: "WhatsApp",
          type: "select",
          options: dropdownData.whatsAppTemplates,
          selected: row?.whatsappTemplate || "",
        },
        {
          name: "smsTemplate",
          label: "SMS",
          type: "select",
          options: dropdownData.smsTemplates,
          selected: row?.smsTemplate || "",
        },
        {
          name: "emailTemplate",
          label: "Email",
          type: "select",
          options: dropdownData.emailTemplates,
          selected: row?.emailTemplate || "",
        },
      ],
    });
  };

  const handleTemplateSubmit = async (values) => {
    const record = templateDialog.record;
    if (!record || !record.id) {
      setTemplateDialog((prev) => ({ ...prev, open: false }));
      return;
    }

    try {
      const payload = {
        ...record,
        whatsappTemplate: values.whatsappTemplate ?? record.whatsappTemplate,
        smsTemplate: values.smsTemplate ?? record.smsTemplate,
        emailTemplate: values.emailTemplate ?? record.emailTemplate,
      };

      await updateStatus(record.id, payload);
      await loadNotificationData();
    } catch (e) {
      console.error("Failed to update templates", e);
    } finally {
      setTemplateDialog((prev) => ({ ...prev, open: false }));
    }
  };

  return (
    <div className="min-h-full bg-gray-50 px-4 py-6">
      <div className="space-y-5">
        {/* Top Action Bar */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 max-w-md relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by event, module, sender, receiver..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804] bg-white"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">SR.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Module</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Sender</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wide">WhatsApp</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wide">SMS</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wide">Email</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wide">Template</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-[#f08804] rounded-full animate-spin"></div>
                        <p className="text-sm font-medium">Loading...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <Bell className="w-8 h-8 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">No records found</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {filterText ? "Try adjusting your search terms" : "No notification settings available"}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row, idx) => (
                    <tr key={row.id || idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{idx + 1}</td>
                      <td className="px-4 py-3 text-gray-800 font-medium">{row.eventId}</td>
                      <td className="px-4 py-3 text-gray-700">{row.module}</td>
                      <td className="px-4 py-3 text-gray-700">{row.sender}</td>

                      {/* WhatsApp toggle */}
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={!!row.whatsapp}
                          onChange={(e) =>
                            handleToggle(row, "whatsapp", e.target.checked)
                          }
                          className="h-4 w-4 accent-[#f08804] cursor-pointer"
                        />
                      </td>

                      {/* SMS toggle */}
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={!!row.sms}
                          onChange={(e) =>
                            handleToggle(row, "sms", e.target.checked)
                          }
                          className="h-4 w-4 accent-[#f08804] cursor-pointer"
                        />
                      </td>

                      {/* Email toggle */}
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={!!row.email}
                          onChange={(e) =>
                            handleToggle(row, "email", e.target.checked)
                          }
                          className="h-4 w-4 accent-[#f08804] cursor-pointer"
                        />
                      </td>

                      {/* Template Icons */}
                      <td className="px-4 py-3 text-center">
                        <div className="inline-flex items-center gap-2 text-gray-500">
                          <button
                            type="button"
                            disabled={!row.whatsappTemplate}
                            onClick={() => openTemplateDialog("whatsapp", row)}
                            className={`p-1.5 rounded-lg hover:bg-emerald-50 ${
                              row.whatsappTemplate
                                ? "cursor-pointer hover:text-emerald-600"
                                : "opacity-40 cursor-not-allowed"
                            }`}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </button>

                          <button
                            type="button"
                            disabled={!row.smsTemplate}
                            onClick={() => openTemplateDialog("sms", row)}
                            className={`p-1.5 rounded-lg hover:bg-[#FFF4E6] ${
                              row.smsTemplate
                                ? "cursor-pointer hover:text-[#C45500]"
                                : "opacity-40 cursor-not-allowed"
                            }`}
                          >
                            <Smartphone className="w-4 h-4" />
                          </button>

                          <button
                            type="button"
                            disabled={!row.emailTemplate}
                            onClick={() => openTemplateDialog("email", row)}
                            className={`p-1.5 rounded-lg hover:bg-amber-50 ${
                              row.emailTemplate
                                ? "cursor-pointer hover:text-amber-500"
                                : "opacity-40 cursor-not-allowed"
                            }`}
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => openAddEditDialog(row)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-[#C45500] hover:bg-[#FFF4E6] px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results count */}
        {!loading && filteredRows.length > 0 && (
          <div className="px-2">
            <p className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-900">{filteredRows.length}</span>{" "}
              of{" "}
              <span className="font-semibold text-gray-900">{rows.length}</span>{" "}
              notifications
              {filterText && " (filtered)"}
            </p>
          </div>
        )}
      </div>

      {/* Template Dialog (View or Edit) */}
      {templateDialog.open && (
        <NotificationTemplateDialog
          type={templateDialog.type}
          title={templateDialog.title}
          message={templateDialog.message}
          fields={templateDialog.fields}
          record={templateDialog.record}
          onClose={() =>
            setTemplateDialog((prev) => ({ ...prev, open: false }))
          }
          // Only show Save for edit/add template mode
          onSubmit={
            templateDialog.type === "template"
              ? handleTemplateSubmit
              : undefined
          }
        />
      )}
    </div>
  );
}
